import { useCallback, useEffect, useMemo, useState } from "react"
import type { Template } from "shared"
import {
	adminGetTemplates,
	adminAddTemplate,
	useMountEffect,
	getDateFromUnix,
	adminGetDefaultTemplates,
	DEFAULT_AVAILABLE_CUSTOM_TAGS,
	clientApi,
	naturalSortComparer,
	DEFAULT_AVAILABLE_TEMPLATES,
	useUpdateableHandler,
	CardView,
	CustomDropdown,
	SortableTableCard,
	SearchBar,
	useSearch,
} from "shared"
import { InlineLabelSwitch } from "shared/src/components/shadcn/components/atoms/switches"
import { Tooltip, TooltipTrigger, TooltipContent } from "shared/src/components/shadcn/components/ui/tooltip"
import { Button } from "shared/src/components/shadcn/components/atoms/buttons"
import { useHistory } from "react-router-dom"

export function TemplatesPage({ clientId, data, setData }) {
	const [waiting, setWaiting] = useState<boolean>(false)
	const [addingTemplate, setAddingTemplate] = useState<boolean>(false)
	const [templates, setTemplates] = useState<Template[]>([])
	const [selectedDefaultTemplate, setSelectedDefaultTemplate] = useState<string>(null)
	const [defaultTemplates, setDefaultTemplates] = useState<any[]>([])

	const availableTemplates = data?.limits?.templates?.limit ?? DEFAULT_AVAILABLE_TEMPLATES
	const numCreatedTemplates = templates.filter((template) => template.live !== false).length
	const [localAvailableTemplates, handleLocalAvailableTemplates] = useUpdateableHandler(availableTemplates)
	const [localEnforceTemplateLimit, setLocalEnforceTemplateLimit] = useState(data?.limits?.templates?.enforced || false)
	const [isLoading, setIsLoading] = useState(false)

	const makeSearchString = (template: Template) => {
		return `${template.templateName} `
	}

	const [searchTermRef, setSearchTerm, filteredDefaultTemplates] = useSearch(
		defaultTemplates,
		makeSearchString,
		"default-templates-search"
	)

	const updateTemplateLimit = (updates) => {
		setIsLoading(true)
		const newLimits = {
			...data.limits,
			templates: {
				...data?.limits?.templates,
				...updates,
			},
		}
		const change = {
			limits: {
				templates: {
					...updates,
				},
			},
		}

		const newData = { limits: newLimits }
		clientApi
			.patch({ clientId, updates: change, changesOnly: true })
			.then(() => {
				setData({ ...data, ...newData })
			})
			.catch(() => {
				alert("Failed to update. Refresh page to check current data")
			})
			.finally(() => {
				setIsLoading(false)
			})
	}

	const setNewTemplateLimit = () => {
		updateTemplateLimit({ limit: parseInt(localAvailableTemplates) })
	}

	const setEnforceTemplateLimit = (newValue) => {
		updateTemplateLimit({ limit: parseInt(localAvailableTemplates), enforced: newValue })
		setLocalEnforceTemplateLimit(newValue)
	}

	const tagsToAdd = useMemo(
		() =>
			Object.keys(
				defaultTemplates.find((template) => template.default_template_id === selectedDefaultTemplate)?.customTags ?? {}
			),
		[defaultTemplates, selectedDefaultTemplate]
	)
	const numTags = tagsToAdd.length ?? 0
	const currentTags = useMemo(() => Object.keys(data.options?.customTags?.tags ?? {}), [data.options?.customTags?.tags])
	const currentNumTags = currentTags.length ?? 0
	const availableTags =
		data?.limits?.tags?.limit ?? data?.options?.customTags?.availableTags ?? DEFAULT_AVAILABLE_CUSTOM_TAGS
	const tooManyTagsInTemplateLimitEnforced = useMemo(
		() => new Set([...tagsToAdd, ...currentTags]).size > availableTags && data?.limits?.tags?.enforced,
		[availableTags, currentTags, tagsToAdd, data?.limits?.tags?.enforced]
	)
	const tooManyTagsInTemplate = useMemo(
		() => new Set([...tagsToAdd, ...currentTags]).size > availableTags,
		[availableTags, currentTags, tagsToAdd]
	)
	const defaultTemplateWarning = useMemo(() => {
		if (numTags > 0 && tooManyTagsInTemplateLimitEnforced) {
			return `Client has ${currentNumTags}/${availableTags} available custom tags and tags limits are enforced. This template has ${numTags} custom tag${
				numTags === 1 ? "" : "s"
			}. To apply this template you need to increase the available tags.`
		} else if (numTags > 0 && tooManyTagsInTemplate) {
			return `Client has ${currentNumTags}/${availableTags} available custom tags. This template has ${numTags} custom tag${
				numTags === 1 ? "" : "s"
			}. By adding this template, the client will exceed the available tags.`
		}
		return null
	}, [availableTags, currentNumTags, numTags, tooManyTagsInTemplateLimitEnforced, tooManyTagsInTemplate])

	const fetchTemplates = useCallback(
		(event?: React.MouseEvent<HTMLButtonElement>) => {
			setWaiting(true)
			adminGetTemplates({ clientId, attributes: ["templateName", "createdTime", "fromTemplate", "live"] })
				.then((templates) => {
					setTemplates(templates)
				})
				.finally(() => {
					setWaiting(false)
				})

			adminGetDefaultTemplates({ attributes: ["templateName", "customTags"] }).then((res) => {
				setDefaultTemplates(res)
			})
		},
		[clientId]
	)

	const createTemplate = useCallback(() => {
		setAddingTemplate(true)
		adminAddTemplate({ clientId, defaultTemplateId: selectedDefaultTemplate })
			.then((res) => {
				setTemplates((prev) => prev.concat(res))
				fetchTemplates()
			})
			.finally(() => {
				clientApi.get({ clientId, query: ["miscData"] }).then((res) => {
					setData(res)
				})
			})
			.finally(() => {
				setAddingTemplate(false)
			})
	}, [clientId, fetchTemplates, selectedDefaultTemplate, setData])

	useMountEffect(() => {
		// fetch users for client
		fetchTemplates()
	})

	return (
		<div
			className="account-container"
			style={{ maxWidth: "90%", margin: "auto", gap: 16, display: "flex", flexDirection: "column" }}>
			<h1 className="bs-h1">Templates</h1>
			<CardView>
				<h4 className="bs-h4">Set template limits</h4>
				<div style={{ display: "flex", gap: 5, alignItems: "center", margin: "20px 0" }}>
					<span style={numCreatedTemplates > localAvailableTemplates ? { color: "var(--ara-error)" } : {}}>
						Templates Used: {numCreatedTemplates}
					</span>{" "}
					/
					<input
						style={{ width: 40 }}
						type="text"
						inputMode="numeric"
						pattern="[0-9]*"
						value={localAvailableTemplates}
						onChange={handleLocalAvailableTemplates}></input>
					<Button style={{ marginTop: 0 }} loading={isLoading} onClick={setNewTemplateLimit}>
						Set New Limit
					</Button>
				</div>
				<div style={{ width: "fit-content" }}>
					<Tooltip>
						<TooltipTrigger asChild>
							<div style={{ display: "flex", gap: 10, alignItems: "center", margin: "20px 0", width: "fit-content" }}>
								Enforce template limits:{" "}
								<InlineLabelSwitch
									checked={localEnforceTemplateLimit}
									onCheckedChange={(newValue) => setEnforceTemplateLimit(newValue)}
								/>
							</div>
						</TooltipTrigger>
						<TooltipContent side="bottom" className="max-w-[400px]">
							Admins and brand editors will need to delete some live templates or set them to draft if the number of
							live templates exceeds the limit before setting new templates to live.
						</TooltipContent>
					</Tooltip>
				</div>
			</CardView>
			<CardView>
				<h4 className="bs-h4">Add new template from default</h4>
				<div style={{ display: "flex", alignItems: "center", gap: 20 }}>
					<div style={{ minWidth: 250 }}>
						<CustomDropdown
							actions={Object.values(filteredDefaultTemplates)
								.sort((a, b) => naturalSortComparer(a.templateName, b.templateName))
								.map((template) => {
									const numTags = Object.keys(template.customTags ?? {})?.length ?? 0
									return {
										label: `${template.templateName} (${numTags} custom tag${numTags === 1 ? "" : "s"})`,
										key: template.default_template_id,
									}
								})}
							onChange={(e) => {
								setSelectedDefaultTemplate(e.target.value)
							}}
							value={selectedDefaultTemplate}
							bestOption={"Select a default template"}>
							<SearchBar searchTermRef={searchTermRef} setSearchTerm={setSearchTerm} />
						</CustomDropdown>
					</div>
					<Button
						className="btn btn-primary SquarerButton"
						style={{
							backgroundColor: "var(--ara-gold)",
							borderColor: "var(--ara-gold)",
							color: "white",
						}}
						onClick={createTemplate}
						loading={addingTemplate}
						disabled={addingTemplate || selectedDefaultTemplate == null || tooManyTagsInTemplateLimitEnforced}>
						Add
					</Button>
					<div>{defaultTemplateWarning != null && <div className="redText">{defaultTemplateWarning}</div>}</div>
				</div>
			</CardView>
			<TemplateTable
				filteredTemplates={templates}
				isRefreshing={waiting}
				pageNumber={0}
				fetchTemplates={fetchTemplates}
				setTemplates={setTemplates}></TemplateTable>
		</div>
	)
}

function sortByCreationTime(a, b) {
	return b?.createdTime - a?.createdTime
}

function TemplateTable({ filteredTemplates, isRefreshing, pageNumber, fetchTemplates, setTemplates }) {
	const [tableRows, setTableRows] = useState([])
	const history = useHistory()

	useEffect(() => {
		createRows(Object.values(filteredTemplates))
	}, [filteredTemplates])

	function createRows(data) {
		const rows = data.map(createRow)
		setTableRows(rows)
	}

	function createRow(templateData) {
		const templateName =
			templateData.templateName != null && templateData.templateName?.length > 0 ? templateData.templateName : "-"
		const clientId = templateData.client_id ?? templateData.clientId
		return {
			...templateData,
			templateName,
			date: getDateFromUnix(templateData.createdTime).toLocaleString(),
			goToTemplate: (
				<div
					className="blueText clickable"
					onClick={() => {
						history.push(`/client/${clientId}/login?templateId=${templateData.template_id}`)
					}}>
					{"Go to Template -->"}
				</div>
			),
		}
	}

	return (
		<SortableTableCard
			pageNumber={pageNumber}
			headings={["Template Name", "Created Date", ""]}
			info={tableRows}
			rowIdKey="template_id"
			sortFunc={sortByCreationTime}
			infoKeys={["templateName", "date", "goToTemplate"]}
			rowFunc={() => {}}
			refreshProps={{ onClick: fetchTemplates, isRefreshing: isRefreshing }}></SortableTableCard>
	)
}
